import React, {
  createContext, useCallback, useContext, useEffect, useState, type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import type { User } from '../types';
import { authAPI } from '../api/auth';
import { verificationAPI } from '../api/verification';
import userAPI from '../api/user';
import toast from '../utils/toast';

// ---------------------------------------------------------------------------
// Storage helpers — rememberMe toggles localStorage vs sessionStorage
// ---------------------------------------------------------------------------
const isEphemeral = (): boolean =>
  sessionStorage.getItem('ephemeral_session') === '1';

const getStore = (): Storage => (isEphemeral() ? sessionStorage : localStorage);

const getUserCache = (): User | null => {
  try {
    const raw = getStore().getItem('user');
    return raw ? JSON.parse(raw) as User : null;
  } catch {
    return null;
  }
};

const setUserCache = (user: User | null): void => {
  const store = getStore();
  if (user) store.setItem('user', JSON.stringify(user));
  else store.removeItem('user');
};

// ---------------------------------------------------------------------------
// Context types
// ---------------------------------------------------------------------------
interface AuthContextType {
  user: User | null;
  token: string | null;
  groups: string[];
  isLoading: boolean;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  loginWithGoogle: (idToken: string, rememberMe?: boolean) => Promise<void>;
  loginWithPhone: (phone: string, code: string) => Promise<void>;
  register: (userData: Partial<User> & { password: string }) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<Partial<User>>;
  refreshToken: () => Promise<void>;
  updateUser: (updates: Partial<User>) => void;
  hasRole: (role: string) => boolean;
  changePassword: (currentPassword: string, newPassword: string) => Promise<{ success: boolean; error?: string }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [groups, setGroups] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // ---- bootstrap on mount ------------------------------------------------
  useEffect(() => {
    const bootstrap = async () => {
      const hasSession = authAPI.restoreSession();
      if (!hasSession) {
        setIsLoading(false);
        return;
      }
      try {
        const me = await authAPI.me();
        setUser(me);
        setGroups(me.roles ?? []);
      } catch (e) {
        await authAPI.logout();
      } finally {
        setIsLoading(false);
      }
    };
    bootstrap();
  }, []);
  // ---- session-expired event from authAPI -------------------------------
  useEffect(() => {
    const handler = () => {
      setUser(null);
      setGroups([]);
      setUserCache(null);
      navigate('/login');
    };
    window.addEventListener('auth:session-expired', handler);
    return () => window.removeEventListener('auth:session-expired', handler);
  }, [navigate]);

  // ---- shared post-login handler ---------------------------------------
  const finalizeLogin = useCallback(async (
    access: string,
    roles: string[],
    rememberMe: boolean,
  ) => {
    // fetch fresh profile (includes roles from server)
    const me = await authAPI.me();
    const merged: User = { ...me, roles: me.roles ?? roles ?? [] };

    if (rememberMe) sessionStorage.removeItem('ephemeral_session');
    else sessionStorage.setItem('ephemeral_session', '1');

    setUserCache(merged);
    setUser(merged);
    setGroups(merged.roles ?? []);
    setToken(access);
  }, []);

  // ---- login -----------------------------------------------------------
  const login = useCallback(async (
    email: string,
    password: string,
    rememberMe: boolean = true,
  ) => {
    setIsLoading(true);
    try {
      const response = await authAPI.login(email, password, rememberMe);
      await finalizeLogin(response.access, response.roles ?? [], rememberMe);
      if (rememberMe) {
        localStorage.setItem('remember_email', email);
      } else {
        localStorage.removeItem('remember_email');
      }
    } finally {
      setIsLoading(false);
    }
  }, [finalizeLogin]);

  // ---- Google login ----------------------------------------------------
  const loginWithGoogle = useCallback(async (
    idToken: string,
    rememberMe: boolean = true,
  ) => {
    setIsLoading(true);
    try {
      const response = await authAPI.googleLogin(idToken);
      await finalizeLogin(response.access, response.roles ?? [], rememberMe);
    } catch (error: any) {
      toast.error(error.message || 'Google login failed');
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [finalizeLogin]);

  // ---- phone login -----------------------------------------------------
  const loginWithPhone = useCallback(async (phone: string, code: string) => {
    setIsLoading(true);
    try {
      const response = await verificationAPI.verifyPhoneAndLogin(phone, code);
      const { access, refresh, user: u } = response.data;
      if (!access || !refresh) throw new Error('Phone login returned no tokens');

      // persist tokens before authAPI.me() is called by finalizeLogin
      localStorage.setItem('access_token', access);
      localStorage.setItem('refresh_token', refresh);
      sessionStorage.removeItem('ephemeral_session');

      authAPI.setupInterceptors();
      await finalizeLogin(access, u?.roles ?? [], true);
    } catch (error: any) {
      toast.error(error.message || 'Phone login failed');
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [finalizeLogin]);

  // ---- register --------------------------------------------------------
  const register = useCallback(async (userData: Partial<User> & { password: string }) => {
    setIsLoading(true);
    try {
      const response = await authAPI.register(userData);
      await finalizeLogin(response.access, response.roles ?? [], true);
    } catch (error: any) {
      toast.error(error.message || 'Registration failed');
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [finalizeLogin]);

  // ---- logout ----------------------------------------------------------
  const logout = useCallback(async () => {
    await authAPI.logout();
    setUser(null);
    setGroups([]);
    setToken(null);
    setUserCache(null);
  }, []);

  // ---- refresh helpers -------------------------------------------------
  const refreshUser = useCallback(async (): Promise<Partial<User>> => {
    try {
      const response = await userAPI.getProfile();
      if (response.data) {
        const updated: User = {
          ...response.data,
          roles: response.data.roles ?? [],
        };
        setUser(updated);
        setGroups(updated.roles ?? []);
        setUserCache(updated);
        return updated;
      }
      return {};
    } catch (error) {
      console.error('Failed to refresh user:', error);
      return {};
    }
  }, []);

  // Triggered manually by any consumer; authAPI's interceptors already
  // refresh automatically, so this is a force-refresh escape hatch.
  const refreshToken = useCallback(async () => {
    const store = getStore();
    const refresh = store.getItem('refresh_token');
    if (!refresh) throw new Error('No refresh token');
    try {
      // refreshOnce is internal; call the endpoint directly
      const { default: axios } = await import('axios');
      const { API_BASE_URL } = await import('../api/auth');
      const res = await axios.post(
        `${API_BASE_URL}/auth/token/refresh/`,
        { refresh },
      );
      store.setItem('access_token', res.data.access);
      if (res.data.refresh) {
        store.setItem('refresh_token', res.data.refresh);
      }
      setToken(res.data.access);
    } catch (err) {
      await logout();
      throw err;
    }
  }, [logout]);

  // ---- update user -----------------------------------------------------
  const updateUser = useCallback((updates: Partial<User>) => {
    setUser(prev => {
      if (!prev) return null;
      const next = { ...prev, ...updates };
      setUserCache(next);
      if (next.roles) setGroups(next.roles);
      return next;
    });
  }, []);

  // ---- role check ------------------------------------------------------
  const hasRole = useCallback(
    (role: string) => user?.roles?.includes(role) ?? false,
    [user],
  );

  // ---- password change -------------------------------------------------
  const changePassword = useCallback(async (
    currentPassword: string,
    newPassword: string,
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      setIsLoading(true);
      // TODO: current authAPI has initiate+confirm 2FA flow, not a
      // single change_password endpoint. Adapt this once the UI is
      // ready for the 2FA flow.
      const response = await authAPI.initiatePasswordChange({
        current_password: currentPassword,
        channel: 'email',
      });
      return { success: true };
    } catch (error: any) {
      const msg = error?.response?.data?.error || 'Network error';
      return { success: false, error: msg };
    } finally {
      setIsLoading(false);
    }
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      token,
      groups,
      isLoading,
      login,
      loginWithGoogle,
      loginWithPhone,
      register,
      logout,
      refreshUser,
      refreshToken,
      updateUser,
      hasRole,
      changePassword,
    }}>
      {children}
    </AuthContext.Provider>
  );
};